/**
 * Tool help registry — maps a canonical tool key to its drawer content for
 * `ToolHelpButton`. Two forms are supported:
 *
 *   { sections: [...] }   — declarative, i18n-driven (rendered by ToolHelpSections)
 *   { component: () => import(...) }  — a custom Vue component (full override)
 *
 * A tool page can also pass help via the `#content` slot of ToolHelpButton,
 * which takes precedence over the registry. Tools with no entry and no slot
 * render nothing — the help button hides itself.
 *
 * Adding help for a tool = add one entry here (+ locale keys for the sections).
 */

import { defineAsyncComponent } from 'vue';

export const TOOL_HELP = {
  'day-ahead-prediction': {
    sections: [
      {
        titleKey: 'tools.day_ahead.help.title',
        tone: 'primary',
        paragraphs: ['tools.day_ahead.help.paragraph_1_a', 'tools.day_ahead.help.paragraph_2'],
      },
      {
        titleKey: 'tools.day_ahead.help.features.title',
        tone: 'info',
        items: [
          'tools.day_ahead.help.features.scenarios',
          'tools.day_ahead.help.features.extremes',
          'tools.day_ahead.help.features.sources',
        ],
      },
      {
        titleKey: 'tools.day_ahead.help.faq.title',
        items: [
          {
            questionKey: 'tools.day_ahead.help.faq.update.question',
            answerKey: 'tools.day_ahead.help.faq.update.answer',
          },
          {
            questionKey: 'tools.day_ahead.help.faq.intervals.question',
            answerKey: 'tools.day_ahead.help.faq.intervals.answer',
          },
        ],
      },
    ],
  },
  'control-state-prediction': {
    component: defineAsyncComponent(() => import('./ControlStateHelp.vue')),
  },
  'capacity-fee-forecaster': {
    sections: [
      {
        titleKey: 'tools.capacity_fee_forecaster.help.title',
        tone: 'primary',
        paragraphs: [
          'tools.capacity_fee_forecaster.help.paragraph_1',
          'tools.capacity_fee_forecaster.help.paragraph_2',
        ],
      },
      {
        titleKey: 'tools.capacity_fee_forecaster.help.features.title',
        tone: 'info',
        items: [
          'tools.capacity_fee_forecaster.help.features.products',
          'tools.capacity_fee_forecaster.help.features.horizon',
          'tools.capacity_fee_forecaster.help.features.improving',
        ],
      },
      {
        titleKey: 'tools.capacity_fee_forecaster.help.faq.title',
        items: [
          {
            questionKey: 'tools.capacity_fee_forecaster.help.faq.products.question',
            answerKey: 'tools.capacity_fee_forecaster.help.faq.products.answer',
          },
          {
            questionKey: 'tools.capacity_fee_forecaster.help.faq.interpretation.question',
            answerKey: 'tools.capacity_fee_forecaster.help.faq.interpretation.answer',
          },
        ],
      },
    ],
  },
  'spike-probability': {
    sections: [
      {
        titleKey: 'tools.spike_probability.help.title',
        tone: 'primary',
        paragraphs: [
          'tools.spike_probability.help.paragraph_1',
          'tools.spike_probability.help.paragraph_2',
        ],
      },
      {
        titleKey: 'tools.spike_probability.help.how_to.title',
        items: [
          'tools.spike_probability.help.how_to.distribution',
          'tools.spike_probability.help.how_to.price_line',
          'tools.spike_probability.help.how_to.risk',
        ],
      },
    ],
  },
  'generation-map': {
    sections: [
      {
        titleKey: 'tools.generation_map.help.title',
        tone: 'primary',
        paragraphs: ['tools.generation_map.help.intro'],
      },
      {
        titleKey: 'tools.generation_map.help.tabs_title',
        items: [
          'tools.generation_map.help.tab_live',
          'tools.generation_map.help.tab_capacity',
          'tools.generation_map.help.tab_compare',
        ],
      },
      {
        titleKey: 'tools.generation_map.help.method_title',
        tone: 'info',
        paragraphs: [
          'tools.generation_map.help.method_intro',
          'tools.generation_map.help.method_weather',
        ],
        items: [
          'tools.generation_map.help.method_solar',
          'tools.generation_map.help.method_wind',
          'tools.generation_map.help.method_forecast',
        ],
      },
      {
        titleKey: 'tools.generation_map.help.data_title',
        items: [
          {
            labelKey: 'tools.generation_map.help.data_solar.label',
            textKey: 'tools.generation_map.help.data_solar.text',
          },
          {
            labelKey: 'tools.generation_map.help.data_wind.label',
            textKey: 'tools.generation_map.help.data_wind.text',
          },
          {
            labelKey: 'tools.generation_map.help.data_weather.label',
            textKey: 'tools.generation_map.help.data_weather.text',
          },
        ],
      },
      {
        titleKey: 'tools.generation_map.help.caveats_title',
        items: [
          'tools.generation_map.help.caveat_modelled',
          'tools.generation_map.help.caveat_coverage',
          'tools.generation_map.help.caveat_time',
          'tools.generation_map.help.caveat_granularity',
        ],
      },
    ],
  },
  'hedging-benchmark': {
    sections: [
      // The historical route first, in the order the page asks its questions.
      {
        titleKey: 'tools.hedging_benchmark.help.historical.route.title',
        tone: 'primary',
        items: [
          {
            labelKey: 'tools.hedging_benchmark.help.historical.route.profile_label',
            textKey: 'tools.hedging_benchmark.help.historical.route.profile_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.route.product_label',
            textKey: 'tools.hedging_benchmark.help.historical.route.product_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.route.strategy_label',
            textKey: 'tools.hedging_benchmark.help.historical.route.strategy_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.route.results_label',
            textKey: 'tools.hedging_benchmark.help.historical.route.results_text',
          },
        ],
      },
      {
        titleKey: 'tools.hedging_benchmark.help.historical.products.title',
        items: [
          {
            labelKey: 'tools.hedging_benchmark.help.historical.products.profile_label',
            textKey: 'tools.hedging_benchmark.help.historical.products.profile_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.products.block_label',
            textKey: 'tools.hedging_benchmark.help.historical.products.block_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.products.price_label',
            textKey: 'tools.hedging_benchmark.help.historical.products.price_text',
          },
        ],
      },
      {
        titleKey: 'tools.hedging_benchmark.help.historical.reading.title',
        items: [
          {
            labelKey: 'tools.hedging_benchmark.help.historical.reading.map_label',
            textKey: 'tools.hedging_benchmark.help.historical.reading.map_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.reading.saving_label',
            textKey: 'tools.hedging_benchmark.help.historical.reading.saving_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.reading.months_label',
            textKey: 'tools.hedging_benchmark.help.historical.reading.months_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.reading.advanced_label',
            textKey: 'tools.hedging_benchmark.help.historical.reading.advanced_text',
          },
        ],
      },
      {
        titleKey: 'tools.hedging_benchmark.help.historical.badges.title',
        tone: 'info',
        items: [
          {
            labelKey: 'tools.hedging_benchmark.help.historical.badges.partial_label',
            textKey: 'tools.hedging_benchmark.help.historical.badges.partial_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.badges.simulated_label',
            textKey: 'tools.hedging_benchmark.help.historical.badges.simulated_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.badges.provisional_label',
            textKey: 'tools.hedging_benchmark.help.historical.badges.provisional_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.badges.hindsight_label',
            textKey: 'tools.hedging_benchmark.help.historical.badges.hindsight_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.historical.badges.model_label',
            textKey: 'tools.hedging_benchmark.help.historical.badges.model_text',
          },
        ],
      },
      {
        titleKey: 'tools.hedging_benchmark.help.about.title',
        tone: 'primary',
        paragraphs: [
          'tools.hedging_benchmark.help.about.p1',
          'tools.hedging_benchmark.help.about.p2',
        ],
      },
      {
        titleKey: 'tools.hedging_benchmark.help.steps.title',
        items: [
          {
            labelKey: 'tools.hedging_benchmark.help.steps.profile_label',
            textKey: 'tools.hedging_benchmark.help.steps.profile_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.steps.years_label',
            textKey: 'tools.hedging_benchmark.help.steps.years_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.steps.strategy_label',
            textKey: 'tools.hedging_benchmark.help.steps.strategy_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.steps.advice_label',
            textKey: 'tools.hedging_benchmark.help.steps.advice_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.steps.results_label',
            textKey: 'tools.hedging_benchmark.help.steps.results_text',
          },
        ],
      },
      {
        titleKey: 'tools.hedging_benchmark.help.objectives.title',
        tone: 'info',
        items: [
          {
            labelKey: 'tools.hedging_benchmark.advice.objectives.balanced',
            textKey: 'tools.hedging_benchmark.advice.objective_descriptions.balanced',
          },
          {
            labelKey: 'tools.hedging_benchmark.advice.objectives.lowest_average_cost',
            textKey: 'tools.hedging_benchmark.advice.objective_descriptions.lowest_average_cost',
          },
          {
            labelKey: 'tools.hedging_benchmark.advice.objectives.budget_stability',
            textKey: 'tools.hedging_benchmark.advice.objective_descriptions.budget_stability',
          },
        ],
      },
      {
        titleKey: 'tools.hedging_benchmark.help.products.title',
        tone: 'info',
        paragraphs: ['tools.hedging_benchmark.help.products.intro'],
        items: [
          {
            labelKey: 'tools.hedging_benchmark.spot_products.weighted_spot.label',
            textKey: 'tools.hedging_benchmark.spot_products.weighted_spot.description',
          },
          {
            labelKey: 'tools.hedging_benchmark.spot_products.daily_average.label',
            textKey: 'tools.hedging_benchmark.spot_products.daily_average.description',
          },
          {
            labelKey: 'tools.hedging_benchmark.spot_products.monthly_average.label',
            textKey: 'tools.hedging_benchmark.spot_products.monthly_average.description',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.products.profile_cost_label',
            textKey: 'tools.hedging_benchmark.help.products.profile_cost_text',
          },
        ],
      },
      {
        titleKey: 'tools.hedging_benchmark.help.data.title',
        items: [
          {
            labelKey: 'tools.hedging_benchmark.help.data.forward_label',
            textKey: 'tools.hedging_benchmark.help.data.forward_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.data.spot_label',
            textKey: 'tools.hedging_benchmark.help.data.spot_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.data.timing_label',
            textKey: 'tools.hedging_benchmark.help.data.timing_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.data.privacy_label',
            textKey: 'tools.hedging_benchmark.help.data.privacy_text',
          },
        ],
      },
      {
        titleKey: 'tools.hedging_benchmark.help.reading.title',
        items: [
          {
            labelKey: 'tools.hedging_benchmark.help.reading.effective_label',
            textKey: 'tools.hedging_benchmark.help.reading.effective_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.reading.premium_label',
            textKey: 'tools.hedging_benchmark.help.reading.premium_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.reading.hindsight_label',
            textKey: 'tools.hedging_benchmark.help.reading.hindsight_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.reading.walk_forward_label',
            textKey: 'tools.hedging_benchmark.help.reading.walk_forward_text',
          },
          {
            labelKey: 'tools.hedging_benchmark.help.reading.confidence_label',
            textKey: 'tools.hedging_benchmark.help.reading.confidence_text',
          },
        ],
      },
      {
        titleKey: 'tools.hedging_benchmark.help.limits.title',
        tone: 'warning',
        paragraphs: ['tools.hedging_benchmark.help.limits.intro'],
        items: [
          'tools.hedging_benchmark.help.limits.synthetic',
          'tools.hedging_benchmark.help.limits.excluded_costs',
          'tools.hedging_benchmark.help.limits.scope',
          'tools.hedging_benchmark.help.limits.horizon',
          'tools.hedging_benchmark.help.limits.not_advice',
        ],
      },
      {
        titleKey: 'tools.hedging_benchmark.help.faq.title',
        items: [
          {
            questionKey: 'tools.hedging_benchmark.help.faq.invoice.question',
            answerKey: 'tools.hedging_benchmark.help.faq.invoice.answer',
          },
          {
            questionKey: 'tools.hedging_benchmark.help.faq.full_year.question',
            answerKey: 'tools.hedging_benchmark.help.faq.full_year.answer',
          },
          {
            questionKey: 'tools.hedging_benchmark.help.faq.product_advice.question',
            answerKey: 'tools.hedging_benchmark.help.faq.product_advice.answer',
          },
          {
            questionKey: 'tools.hedging_benchmark.help.faq.act_on_it.question',
            answerKey: 'tools.hedging_benchmark.help.faq.act_on_it.answer',
          },
        ],
      },
    ],
  },
  'imbalance-benchmark': {
    component: defineAsyncComponent(() => import('./ImbalanceBenchmarkHelp.vue')),
  },
  'bidding-strategy': {
    // V4 reference text: plain language, one idea per paragraph.
    sections: [
      {
        titleKey: 'tools.bidding_strategy.help.title',
        tone: 'primary',
        paragraphs: [
          'tools.bidding_strategy.help.about.intro',
          'tools.bidding_strategy.help.about.setup',
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.headline.title',
        tone: 'primary',
        paragraphs: [
          'tools.bidding_strategy.help.headline.text',
          'tools.bidding_strategy.help.headline.per_mw',
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.tiers.title',
        tone: 'info',
        paragraphs: [
          'tools.bidding_strategy.help.tiers.intro',
        ],
        items: [
          {
            labelKey: 'tools.bidding_strategy.help.tiers.achievable.label',
            textKey: 'tools.bidding_strategy.help.tiers.achievable.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.tiers.realistic.label',
            textKey: 'tools.bidding_strategy.help.tiers.realistic.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.tiers.optimal_hindsight.label',
            textKey: 'tools.bidding_strategy.help.tiers.optimal_hindsight.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.tiers.theoretical_maximum.label',
            textKey: 'tools.bidding_strategy.help.tiers.theoretical_maximum.text',
          },
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.gaps.title',
        tone: 'info',
        paragraphs: [
          'tools.bidding_strategy.help.gaps.text',
          'tools.bidding_strategy.help.gaps.range',
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.markets.title',
        tone: 'info',
        paragraphs: [
          'tools.bidding_strategy.help.markets.intro',
        ],
        items: [
          {
            labelKey: 'tools.bidding_strategy.help.markets.day_ahead.label',
            textKey: 'tools.bidding_strategy.help.markets.day_ahead.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.markets.intraday.label',
            textKey: 'tools.bidding_strategy.help.markets.intraday.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.markets.imbalance.label',
            textKey: 'tools.bidding_strategy.help.markets.imbalance.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.markets.fcr.label',
            textKey: 'tools.bidding_strategy.help.markets.fcr.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.markets.afrr.label',
            textKey: 'tools.bidding_strategy.help.markets.afrr.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.markets.mfrr.label',
            textKey: 'tools.bidding_strategy.help.markets.mfrr.text',
          },
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.bidding.title',
        tone: 'info',
        items: [
          {
            labelKey: 'tools.bidding_strategy.help.bidding.limits.label',
            textKey: 'tools.bidding_strategy.help.bidding.limits.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.bidding.virtual.label',
            textKey: 'tools.bidding_strategy.help.bidding.virtual.text',
          },
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.battery.title',
        tone: 'info',
        items: [
          {
            labelKey: 'tools.bidding_strategy.help.battery.capacity.label',
            textKey: 'tools.bidding_strategy.help.battery.capacity.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.battery.power.label',
            textKey: 'tools.bidding_strategy.help.battery.power.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.battery.efficiency.label',
            textKey: 'tools.bidding_strategy.help.battery.efficiency.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.battery.dod.label',
            textKey: 'tools.bidding_strategy.help.battery.dod.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.battery.cycles.label',
            textKey: 'tools.bidding_strategy.help.battery.cycles.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.battery.charge.label',
            textKey: 'tools.bidding_strategy.help.battery.charge.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.battery.costs.label',
            textKey: 'tools.bidding_strategy.help.battery.costs.text',
          },
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.site.title',
        tone: 'info',
        items: [
          {
            labelKey: 'tools.bidding_strategy.help.site.connection.label',
            textKey: 'tools.bidding_strategy.help.site.connection.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.site.site_load.label',
            textKey: 'tools.bidding_strategy.help.site.site_load.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.site.co_optimised.label',
            textKey: 'tools.bidding_strategy.help.site.co_optimised.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.site.load_first.label',
            textKey: 'tools.bidding_strategy.help.site.load_first.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.site.consumption.label',
            textKey: 'tools.bidding_strategy.help.site.consumption.text',
          },
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.site_costs.title',
        tone: 'info',
        paragraphs: [
          'tools.bidding_strategy.help.site_costs.intro',
        ],
        items: [
          {
            labelKey: 'tools.bidding_strategy.help.site_costs.price.label',
            textKey: 'tools.bidding_strategy.help.site_costs.price.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.site_costs.tax.label',
            textKey: 'tools.bidding_strategy.help.site_costs.tax.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.site_costs.estimate.label',
            textKey: 'tools.bidding_strategy.help.site_costs.estimate.text',
          },
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.tariffs.title',
        tone: 'info',
        items: [
          {
            labelKey: 'tools.bidding_strategy.help.tariffs.operators.label',
            textKey: 'tools.bidding_strategy.help.tariffs.operators.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.tariffs.regime.label',
            textKey: 'tools.bidding_strategy.help.tariffs.regime.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.tariffs.capacity.label',
            textKey: 'tools.bidding_strategy.help.tariffs.capacity.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.tariffs.planning.label',
            textKey: 'tools.bidding_strategy.help.tariffs.planning.text',
          },
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.congestion.title',
        tone: 'info',
        paragraphs: [
          'tools.bidding_strategy.help.congestion.intro',
        ],
        items: [
          {
            labelKey: 'tools.bidding_strategy.help.congestion.cbc.label',
            textKey: 'tools.bidding_strategy.help.congestion.cbc.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.congestion.csc.label',
            textKey: 'tools.bidding_strategy.help.congestion.csc.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.congestion.tdtr.label',
            textKey: 'tools.bidding_strategy.help.congestion.tdtr.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.congestion.mandatory.label',
            textKey: 'tools.bidding_strategy.help.congestion.mandatory.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.congestion.payments.label',
            textKey: 'tools.bidding_strategy.help.congestion.payments.text',
          },
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.results.title',
        tone: 'info',
        items: [
          {
            labelKey: 'tools.bidding_strategy.help.results.overview.label',
            textKey: 'tools.bidding_strategy.help.results.overview.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.results.markets.label',
            textKey: 'tools.bidding_strategy.help.results.markets.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.results.tariffs.label',
            textKey: 'tools.bidding_strategy.help.results.tariffs.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.results.daily.label',
            textKey: 'tools.bidding_strategy.help.results.daily.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.results.exports.label',
            textKey: 'tools.bidding_strategy.help.results.exports.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.results.quality.label',
            textKey: 'tools.bidding_strategy.help.results.quality.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.results.length.label',
            textKey: 'tools.bidding_strategy.help.results.length.text',
          },
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.calculation.title',
        tone: 'info',
        paragraphs: [
          'tools.bidding_strategy.help.calculation.plan',
          'tools.bidding_strategy.help.calculation.replay',
          'tools.bidding_strategy.help.calculation.references',
        ],
      },
      {
        titleKey: 'tools.bidding_strategy.help.glossary.title',
        items: [
          {
            labelKey: 'tools.bidding_strategy.help.glossary.isp.label',
            textKey: 'tools.bidding_strategy.help.glossary.isp.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.glossary.soc.label',
            textKey: 'tools.bidding_strategy.help.glossary.soc.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.glossary.gate_closure.label',
            textKey: 'tools.bidding_strategy.help.glossary.gate_closure.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.glossary.capacity_fee.label',
            textKey: 'tools.bidding_strategy.help.glossary.capacity_fee.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.glossary.activation.label',
            textKey: 'tools.bidding_strategy.help.glossary.activation.text',
          },
          {
            labelKey: 'tools.bidding_strategy.help.glossary.physical_virtual.label',
            textKey: 'tools.bidding_strategy.help.glossary.physical_virtual.text',
          },
        ],
      },
    ],
  },
  'forward-benchmark': {
    sections: [
      {
        titleKey: 'tools.forward_benchmark.help.about_title',
        tone: 'primary',
        items: [
          'tools.forward_benchmark.help.about.forward_curves',
          'tools.forward_benchmark.help.about.lp_optimizer',
          'tools.forward_benchmark.help.about.excel_privacy',
        ],
      },
      {
        titleKey: 'tools.forward_benchmark.scenario.section_title',
        tone: 'info',
        items: [
          'tools.forward_benchmark.scenario.version_hint',
          'tools.forward_benchmark.scenario.years_hint',
          'tools.forward_benchmark.scenario.s1_desc',
          'tools.forward_benchmark.scenario.s2_desc',
          'tools.forward_benchmark.scenario.s3_desc',
          'tools.forward_benchmark.scenario.s4_desc',
        ],
      },
      {
        titleKey: 'tools.forward_benchmark.help.faq.title',
        items: [
          {
            questionKey: 'tools.forward_benchmark.help.faq.multi_year.question',
            answerKey: 'tools.forward_benchmark.help.faq.multi_year.answer',
          },
          {
            questionKey: 'tools.forward_benchmark.help.faq.output.question',
            answerKey: 'tools.forward_benchmark.help.faq.output.answer',
          },
        ],
      },
    ],
  },
  'shadow-trading': {
    component: defineAsyncComponent(() => import('./ShadowTradingHelp.vue')),
  },
  'power-market-simulator': {
    sections: [
      {
        titleKey: 'tools.power_market_simulator.help.title',
        tone: 'primary',
        paragraphs: ['tools.power_market_simulator.help.description'],
      },
      {
        titleKey: 'tools.power_market_simulator.help.simple_title',
        tone: 'primary',
        paragraphs: ['tools.power_market_simulator.help.simple_description'],
      },
      {
        titleKey: 'tools.power_market_simulator.help.advanced_title',
        tone: 'warning',
        paragraphs: ['tools.power_market_simulator.help.advanced_description'],
        items: ['tools.power_market_simulator.help.import_description'],
      },
      {
        titleKey: 'tools.power_market_simulator.help.faq.title',
        items: [
          {
            questionKey: 'tools.power_market_simulator.help.faq.use_case.question',
            answerKey: 'tools.power_market_simulator.help.faq.use_case.answer',
          },
          {
            questionKey: 'tools.power_market_simulator.help.faq.limitations.question',
            answerKey: 'tools.power_market_simulator.help.faq.limitations.answer',
          },
        ],
      },
    ],
  },
  orderbook: {
    sections: [
      {
        titleKey: 'tools.orderbook.help_title',
        tone: 'primary',
        paragraphs: ['tools.orderbook.help_intro'],
      },
      {
        titleKey: 'tools.orderbook.depth_title',
        paragraphs: ['tools.orderbook.help_depth'],
      },
      {
        titleKey: 'tools.orderbook.ladder_title',
        paragraphs: ['tools.orderbook.help_ladder'],
      },
      {
        titleKey: 'tools.orderbook.spread_title',
        paragraphs: ['tools.orderbook.help_spread', 'tools.orderbook.help_footer'],
      },
      {
        titleKey: 'tools.orderbook.help_faq_title',
        items: [
          {
            questionKey: 'tools.orderbook.help_faq_liquidity_question',
            answerKey: 'tools.orderbook.help_faq_liquidity_answer',
          },
          {
            questionKey: 'tools.orderbook.help_faq_delay_question',
            answerKey: 'tools.orderbook.help_faq_delay_answer',
          },
        ],
      },
    ],
  },
  'distribution-interruptions': {
    sections: [
      {
        titleKey: 'tools.distribution-interruptions.help.about.title',
        tone: 'primary',
        paragraphs: [
          'tools.distribution-interruptions.help.about.current',
          'tools.distribution-interruptions.help.about.history',
        ],
      },
      {
        titleKey: 'tools.distribution-interruptions.help.sources.title',
        paragraphs: ['tools.distribution-interruptions.help.sources.text'],
      },
      {
        titleKey: 'tools.distribution-interruptions.help.certainty.title',
        tone: 'info',
        items: [
          'tools.distribution-interruptions.help.certainty.identity',
          'tools.distribution-interruptions.help.certainty.impact',
          'tools.distribution-interruptions.help.certainty.geometry',
        ],
      },
      {
        titleKey: 'tools.distribution-interruptions.help.coverage.title',
        tone: 'warning',
        paragraphs: ['tools.distribution-interruptions.help.coverage.text'],
      },
    ],
  },
  'water-risk': {
    sections: [
      {
        titleKey: 'tools.water-risk.help.about.title',
        tone: 'primary',
        paragraphs: ['tools.water-risk.help.about.p1', 'tools.water-risk.help.about.p2'],
      },
      {
        titleKey: 'tools.water-risk.help.sources.title',
        items: [
          {
            labelKey: 'tools.water-risk.help.sources.discharge_label',
            textKey: 'tools.water-risk.help.sources.discharge_text',
          },
          {
            labelKey: 'tools.water-risk.help.sources.live_label',
            textKey: 'tools.water-risk.help.sources.live_text',
          },
          {
            labelKey: 'tools.water-risk.help.sources.bias_label',
            textKey: 'tools.water-risk.help.sources.bias_text',
          },
          {
            labelKey: 'tools.water-risk.help.sources.generation_label',
            textKey: 'tools.water-risk.help.sources.generation_text',
          },
          {
            labelKey: 'tools.water-risk.help.sources.reservoirs_label',
            textKey: 'tools.water-risk.help.sources.reservoirs_text',
          },
        ],
      },
      {
        titleKey: 'tools.water-risk.help.calc.title',
        items: [
          {
            labelKey: 'tools.water-risk.help.calc.status_label',
            textKey: 'tools.water-risk.help.calc.status_text',
          },
          {
            labelKey: 'tools.water-risk.help.calc.at_risk_label',
            textKey: 'tools.water-risk.help.calc.at_risk_text',
          },
          {
            labelKey: 'tools.water-risk.help.calc.ror_label',
            textKey: 'tools.water-risk.help.calc.ror_text',
          },
          {
            labelKey: 'tools.water-risk.help.calc.unknown_label',
            textKey: 'tools.water-risk.help.calc.unknown_text',
          },
        ],
      },
      {
        titleKey: 'tools.water-risk.help.da.title',
        items: [
          {
            labelKey: 'tools.water-risk.help.da.spread_label',
            textKey: 'tools.water-risk.help.da.spread_text',
          },
          {
            labelKey: 'tools.water-risk.help.da.impact_label',
            textKey: 'tools.water-risk.help.da.impact_text',
          },
          {
            labelKey: 'tools.water-risk.help.da.coupling_label',
            textKey: 'tools.water-risk.help.da.coupling_text',
          },
          {
            labelKey: 'tools.water-risk.help.da.calibration_label',
            textKey: 'tools.water-risk.help.da.calibration_text',
          },
        ],
      },
      {
        titleKey: 'tools.water-risk.help.warnings.title',
        paragraphs: [
          'tools.water-risk.help.warnings.disclaimer',
          'tools.water-risk.help.warnings.glofas',
          'tools.water-risk.help.warnings.temperature',
          'tools.water-risk.help.warnings.coverage',
        ],
      },
      {
        titleKey: 'tools.water-risk.help.faq.title',
        items: [
          {
            questionKey: 'tools.water-risk.help.faq.unknown_q',
            answerKey: 'tools.water-risk.help.faq.unknown_a',
          },
          {
            questionKey: 'tools.water-risk.help.faq.live_q',
            answerKey: 'tools.water-risk.help.faq.live_a',
          },
          {
            questionKey: 'tools.water-risk.help.faq.kaub_q',
            answerKey: 'tools.water-risk.help.faq.kaub_a',
          },
        ],
      },
    ],
  },
  'grid-map': {
    sections: [
      {
        titleKey: 'tools.grid_map.help.title',
        tone: 'primary',
        paragraphs: ['tools.grid_map.help.intro'],
      },
      {
        titleKey: 'tools.grid_map.help.layers_title',
        items: [
          'tools.grid_map.help.layer_infrastructure',
          'tools.grid_map.help.layer_generation',
          'tools.grid_map.help.layer_prices',
        ],
      },
      {
        titleKey: 'tools.grid_map.help.header_title',
        tone: 'info',
        paragraphs: ['tools.grid_map.help.header_intro'],
      },
      {
        titleKey: 'tools.grid_map.help.data_title',
        items: ['tools.grid_map.help.data_topology', 'tools.grid_map.help.data_live'],
      },
    ],
  },
  'grid-state': {
    sections: [
      {
        titleKey: 'tools.grid_state.help.about.title',
        tone: 'primary',
        paragraphs: ['tools.grid_state.help.about.p1', 'tools.grid_state.help.about.p2'],
      },
      {
        titleKey: 'tools.grid_state.help.reading.title',
        tone: 'info',
        items: ['tools.grid_state.help.reading.serving_mode', 'tools.grid_state.help.reading.gate'],
      },
    ],
  },
  // PI-488 — main pages (not tools), reusing the same registry + button. The
  // registry key is a lookup string, not a canonical tool id, so this is fine.
  home: {
    sections: [
      {
        titleKey: 'home_cockpit.help.about.title',
        tone: 'primary',
        paragraphs: ['home_cockpit.help.about.p1', 'home_cockpit.help.about.p2'],
      },
      {
        titleKey: 'home_cockpit.help.reading.title',
        tone: 'info',
        items: [
          'home_cockpit.help.reading.news_column',
          'home_cockpit.help.reading.clock',
          'home_cockpit.help.reading.empty_tiles',
        ],
      },
    ],
  },
  news: {
    sections: [
      {
        titleKey: 'news_page.help.about.title',
        tone: 'primary',
        paragraphs: ['news_page.help.about.p1', 'news_page.help.about.p2'],
      },
      {
        titleKey: 'news_page.help.filtering.title',
        tone: 'info',
        items: [
          'news_page.help.filtering.scope',
          'news_page.help.filtering.front_page',
          'news_page.help.filtering.time_window',
        ],
      },
    ],
  },
  status: {
    sections: [
      {
        titleKey: 'status_page.help.about.title',
        tone: 'primary',
        paragraphs: ['status_page.help.about.p1', 'status_page.help.about.p2'],
      },
      {
        titleKey: 'status_page.help.reading.title',
        tone: 'info',
        items: [
          'status_page.help.reading.overview',
          'status_page.help.reading.last_checked',
          'status_page.help.reading.dataset_link',
        ],
      },
    ],
  },
  favorites: {
    sections: [
      {
        titleKey: 'favorites_page.help.about.title',
        tone: 'primary',
        paragraphs: ['favorites_page.help.about.p1', 'favorites_page.help.about.p2'],
      },
    ],
  },
  notifications: {
    sections: [
      {
        titleKey: 'notifications.common.help.about.title',
        tone: 'primary',
        paragraphs: ['notifications.common.help.about.p1'],
      },
      {
        titleKey: 'notifications.common.help.channels.title',
        tone: 'info',
        items: [
          'notifications.common.help.channels.in_app',
          'notifications.common.help.channels.email',
          'notifications.common.help.channels.slack',
          'notifications.common.help.channels.telegram',
          'notifications.common.help.channels.webhook',
          'notifications.common.help.channels.web_push',
          'notifications.common.help.channels.configure',
        ],
      },
    ],
  },
  'my-environment': {
    sections: [
      {
        titleKey: 'mijn_omgeving.help.about.title',
        tone: 'primary',
        paragraphs: ['mijn_omgeving.help.about.p1', 'mijn_omgeving.help.about.p2'],
      },
      {
        titleKey: 'mijn_omgeving.help.features.title',
        tone: 'info',
        items: [
          'mijn_omgeving.help.features.dashboards',
          'mijn_omgeving.help.features.kpis',
          'mijn_omgeving.help.features.charts',
          'mijn_omgeving.help.features.toolruns',
        ],
      },
    ],
  },
};

export function getToolHelp(toolKey) {
  return TOOL_HELP[toolKey] || null;
}
